import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Button, Input, Spinner, Text, Field, MessageBar, MessageBarBody, Dropdown, Option, Tab, TabList, Checkbox, } from '@fluentui/react-components';
import SignaturePad from 'signature_pad';
import { Shell } from '@/components/Shell';
import { useAuth } from '@/hooks/useAuth';
import { Login } from '@/views/Login';
import { api, ApiError } from '@/lib/api';
import { insertSignatureImage } from '@/lib/office';
const SIZES = {
    small: { w: 150, h: 60, label: 'Small (150×60)' },
    medium: { w: 200, h: 80, label: 'Medium (200×80)' },
    large: { w: 280, h: 110, label: 'Large (280×110)' },
};
export function InsertSignature() {
    const { authenticated } = useAuth();
    if (!authenticated)
        return _jsx(Login, { message: "Sign in to use your saved signatures." });
    return _jsx(Picker, {});
}
function Picker() {
    const [tab, setTab] = useState('saved');
    const [size, setSize] = useState('medium');
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);
    return (_jsxs(Shell, { title: "Insert signature", subtitle: "Position your cursor in the document, then pick a signature.", children: [_jsx(Field, { label: "Size", style: { marginBottom: 10 }, children: _jsx(Dropdown, { value: SIZES[size].label, selectedOptions: [size], onOptionSelect: (_, d) => d.optionValue && setSize(d.optionValue), size: "small", children: Object.keys(SIZES).map((s) => (_jsx(Option, { value: s, children: SIZES[s].label }, s))) }) }), _jsxs(TabList, { selectedValue: tab, onTabSelect: (_, d) => setTab(d.value), size: "small", children: [_jsx(Tab, { value: "saved", children: "Saved" }), _jsx(Tab, { value: "draw", children: "Draw new" }), _jsx(Tab, { value: "type", children: "Type new" })] }), info && (_jsx(MessageBar, { intent: "success", style: { marginTop: 10 }, children: _jsx(MessageBarBody, { children: info }) })), error && (_jsx(MessageBar, { intent: "error", style: { marginTop: 10 }, children: _jsx(MessageBarBody, { children: error }) })), _jsxs("div", { style: { marginTop: 12 }, children: [tab === 'saved' && _jsx(SavedTab, { size: size, onInserted: (name) => setInfo(`Inserted "${name}".`), onError: setError }), tab === 'draw' && _jsx(DrawTab, { size: size, onInserted: () => setInfo('Inserted signature.'), onError: setError }), tab === 'type' && _jsx(TypeTab, { size: size, onInserted: () => setInfo('Inserted signature.'), onError: setError })] })] }));
}
function SavedTab({ size, onInserted, onError }) {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [inserting, setInserting] = useState(null);
    useEffect(() => {
        void (async () => {
            try {
                const res = await api.listSignatures();
                setItems(res.signatures);
            }
            catch (e) {
                onError(e instanceof ApiError ? e.message : 'Couldn\'t load signatures.');
            }
            finally {
                setLoading(false);
            }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const insert = async (id, name) => {
        setInserting(id);
        try {
            const { base64 } = await api.getSignatureImage(id);
            const { w, h } = SIZES[size];
            await insertSignatureImage(base64, w, h);
            onInserted(name);
        }
        catch (e) {
            onError(e instanceof Error ? e.message : 'Insert failed.');
        }
        finally {
            setInserting(null);
        }
    };
    if (loading)
        return _jsx(Spinner, { size: "small", label: "Loading saved signatures\u2026" });
    if (items.length === 0) {
        return (_jsxs(Text, { size: 200, style: { color: '#605e5c' }, children: ["You don\u2019t have any saved signatures yet. Switch to ", _jsx("em", { children: "Draw new" }), " or ", _jsx("em", { children: "Type new" }), " to create one."] }));
    }
    return (_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: 8 }, children: items.map((sig) => (_jsxs(Button, { appearance: "outline", onClick: () => insert(sig.id, sig.name), disabled: inserting === sig.id, style: { justifyContent: 'space-between' }, children: [_jsxs("span", { style: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }, children: [_jsx("span", { children: sig.name }), sig.is_default && _jsx(Text, { size: 100, style: { color: '#605e5c' }, children: "Default" })] }), _jsx("span", { style: { fontSize: 11, color: '#605e5c' }, children: "Insert \u2192" })] }, sig.id))) }));
}
function DrawTab({ size, onInserted, onError }) {
    const canvasRef = useRef(null);
    const padRef = useRef(null);
    const [save, setSave] = useState(true);
    const [name, setName] = useState('My signature');
    const [inserting, setInserting] = useState(false);
    useEffect(() => {
        const c = canvasRef.current;
        if (!c)
            return;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        c.width = c.offsetWidth * ratio;
        c.height = c.offsetHeight * ratio;
        c.getContext('2d')?.scale(ratio, ratio);
        padRef.current = new SignaturePad(c, { backgroundColor: 'rgba(0,0,0,0)', penColor: '#062b5f' });
    }, []);
    const clear = () => padRef.current?.clear();
    const insert = async () => {
        if (!padRef.current || padRef.current.isEmpty()) {
            onError('Draw a signature first.');
            return;
        }
        setInserting(true);
        try {
            const dataUrl = padRef.current.toDataURL('image/png');
            const base64 = dataUrl.split(',')[1] ?? '';
            const { w, h } = SIZES[size];
            await insertSignatureImage(base64, w, h);
            if (save) {
                try {
                    await api.saveSignature(name.trim() || 'My signature', base64);
                }
                catch (e) { /* non-fatal: insert succeeded */
                    void e;
                }
            }
            padRef.current.clear();
            onInserted();
        }
        catch (e) {
            onError(e instanceof Error ? e.message : 'Insert failed.');
        }
        finally {
            setInserting(false);
        }
    };
    return (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: 8 }, children: [_jsx("canvas", { ref: canvasRef, style: {
                    width: '100%', height: 120, background: 'white',
                    border: '1px dashed #c2d0e2', borderRadius: 6, touchAction: 'none',
                } }), _jsx("div", { style: { display: 'flex', gap: 8 }, children: _jsx(Checkbox, { checked: save, onChange: (_, d) => setSave(!!d.checked), label: "Save to my account" }) }), save && (_jsx(Input, { size: "small", value: name, onChange: (_, d) => setName(d.value), placeholder: "Signature name" })), _jsxs("div", { style: { display: 'flex', gap: 8 }, children: [_jsx(Button, { onClick: clear, children: "Clear" }), _jsx(Button, { appearance: "primary", onClick: insert, disabled: inserting, style: { flex: 1 }, children: inserting ? 'Inserting…' : 'Insert →' })] })] }));
}
function TypeTab({ size, onInserted, onError }) {
    const [name, setName] = useState('');
    const [save, setSave] = useState(false);
    const [inserting, setInserting] = useState(false);
    const renderToCanvas = (text) => {
        const c = document.createElement('canvas');
        const w = 400, h = 140;
        c.width = w;
        c.height = h;
        const ctx = c.getContext('2d');
        ctx.fillStyle = 'transparent';
        ctx.clearRect(0, 0, w, h);
        // The Caveat font has to be available — Word may not have it. We list a
        // chain ending in cursive so the result is at least script-like.
        ctx.font = '64px "Caveat", "Brush Script MT", "Lucida Handwriting", cursive';
        ctx.fillStyle = '#062b5f';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, 20, h / 2);
        return c.toDataURL('image/png').split(',')[1] ?? '';
    };
    const insert = async () => {
        const trimmed = name.trim();
        if (!trimmed) {
            onError('Type your name first.');
            return;
        }
        setInserting(true);
        try {
            const base64 = renderToCanvas(trimmed);
            const { w, h } = SIZES[size];
            await insertSignatureImage(base64, w, h);
            if (save) {
                try {
                    await api.saveSignature(trimmed, base64);
                }
                catch { /* non-fatal */ }
            }
            onInserted();
        }
        catch (e) {
            onError(e instanceof Error ? e.message : 'Insert failed.');
        }
        finally {
            setInserting(false);
        }
    };
    return (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: 10 }, children: [_jsx(Input, { size: "small", value: name, onChange: (_, d) => setName(d.value), placeholder: "Your name (e.g. Sarah Kim)" }), name.trim() && (_jsx("div", { style: {
                    background: 'white', border: '1px solid #edebe9', borderRadius: 6,
                    padding: '16px 12px', textAlign: 'center',
                    fontFamily: '"Caveat", "Brush Script MT", "Lucida Handwriting", cursive',
                    fontSize: 36, color: '#062b5f', lineHeight: 1,
                }, children: name.trim() })), _jsx(Checkbox, { checked: save, onChange: (_, d) => setSave(!!d.checked), label: "Save to my account" }), _jsx(Button, { appearance: "primary", onClick: insert, disabled: inserting || !name.trim(), children: inserting ? 'Inserting…' : 'Insert →' })] }));
}
